// src/pages/Subscription.jsx
import React, { useEffect, useState } from "react";
import axios from "../services/api";
import {
  subscribe,
  openPortalChange,
  openPortalCancel,
  billingPortal,
} from "../services/subscriptionApi";

const TIERS = [
  { id: "ENTRY",     name: "Entry",     priceMonthly: 39.99,  blurb: "Great for getting started." },
  { id: "MIDDLE",    name: "Middle",    priceMonthly: 59.99,  blurb: "More features, better value.", popular: true },
  { id: "HEADLINER", name: "Headliner", priceMonthly: 149.99, blurb: "All features, priority support." },
];

const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const periodLabel = (status) => {
  if (status === "ACTIVE") return "Renews on";
  if (status === "CANCELLED") return "Ends on";
  return "Period ends on";
};

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

export default function Subscription() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchUser(); }, []);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/account/me");
      setUser(res.data);
    } catch {
      setError("Failed to load user data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    setProcessing(true);
    try {
      const res = await subscribe(tier);
      window.location.href = res.data;
    } catch {
      alert("Failed to start subscription.");
    } finally {
      setProcessing(false);
    }
  };

  const handleChangePlan = async () => {
    setProcessing(true);
    try {
      const res = await openPortalChange();
      window.location.href = res.data;
    } catch {
      alert("Failed to open change-plan portal.");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelInPortal = async () => {
    setProcessing(true);
    try {
      const res = await openPortalCancel();
      window.location.href = res.data;
    } catch {
      alert("Failed to open cancel portal.");
    } finally {
      setProcessing(false);
    }
  };

  const handleBilling = async () => {
    try {
      const res = await billingPortal();
      window.location.href = res.data;
    } catch {
      alert("Failed to open billing portal.");
    }
  };

  if (loading) return <div className="page"><div className="card">Loading…</div></div>;
  if (error)   return <div className="page"><div className="card bg-red-500/10 border-red-500/20 text-red-200">{error}</div></div>;
  if (!user)   return null;

  const {
    subscriptionTier,
    subscriptionStatus,
    verificationStatus,
    subscriptionStartDate,
    subscriptionExpiresAt,
    stripeCustomerId,
  } = user;

  const isActive          = subscriptionStatus === "ACTIVE";
  const isCancelScheduled = subscriptionStatus === "CANCELLED";
  const hasAnySub         = !!subscriptionTier && subscriptionTier !== "NONE";
  const isVerified        = verificationStatus === "VERIFIED";
  const isUnderage        = verificationStatus === "UNDERAGE";
  const isPending         = verificationStatus === "PENDING";

  // We always show the grid:
  const canSubscribeNow = !isUnderage && !hasAnySub;     // show Subscribe buttons only in this case
  const showCompareGrid = hasAnySub || canSubscribeNow;  // render grid for both states

  return (
    <div className="page space-y-6">
      {/* Header */}
      <section className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white">Subscribe</h1>
            <p className="text-slate-300 mt-1">
              Are you a DJ or producer with a unique sound that resonates with our events, looking to break through in the industry?
              We might be the perfect fit! For more details, please contact us directly at: 
              hlt-mgmt@hey-lets-travel.com
            </p>
          </div>
          {stripeCustomerId && (
            <button onClick={handleBilling} className="btn-secondary">
              Billing &amp; Payment Methods
            </button>
          )}
        </div>
      </section>

      {/* Alerts */}
      {isUnderage && (
        <div className="card bg-red-500/10 border-red-500/20 text-red-200">
          Access is disabled due to age verification failure.
        </div>
      )}
      {isPending && (
        <div className="card bg-amber-500/10 border-amber-400/20 text-amber-200">
          Your age verification is pending. You can still subscribe—benefits unlock once verified.
        </div>
      )}
      {subscriptionStatus === "FAILED" && (
        <div className="card bg-yellow-500/10 border-yellow-500/20 text-yellow-200">
          Subscription payment failed.{" "}
          <button onClick={handleBilling} className="underline font-semibold">
            Update billing info
          </button>
          .
        </div>
      )}

      {/* Plan grid — always visible */}
      {showCompareGrid && (
        <section>
          <div className="flex items-end justify-between mb-3">
            <h2 className="text-lg font-semibold text-white">
              {canSubscribeNow ? "Choose a plan" : "Compare plans"}
            </h2>
            {!canSubscribeNow && (
              <p className="text-xs text-slate-400">
                You’re on <span className="font-medium">{subscriptionTier}</span>. Review plans below, then use{" "}
                <span className="font-medium">Change plan</span>.
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TIERS.map((t) => (
              <TierCard
                key={t.id}
                tier={t}
                // buttons only when user can subscribe now
                showCTA={canSubscribeNow}
                disabled={processing || isUnderage}
                onSelect={() => handleSubscribe(t.id)}
                showVerifyHint={canSubscribeNow && !isVerified}
                isCurrent={hasAnySub && t.id === subscriptionTier}
              />
            ))}
          </div>
        </section>
      )}

      {/* Current subscription summary + actions */}
      {hasAnySub && (
        <section className="card">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-semibold text-white">
                    Current plan: {subscriptionTier}
                  </span>
                  {subscriptionStatus && (
                    <span
                      className={`badge ${
                        isActive ? "bg-green-500/20 text-green-200" : "bg-white/10 text-slate-200"
                      }`}
                    >
                      {subscriptionStatus}
                    </span>
                  )}
                </div>

                <div className="text-sm text-slate-300 mt-2 space-y-1">
                  {subscriptionStartDate && (
                    <p><span className="text-slate-400">Started:</span> {new Date(subscriptionStartDate).toLocaleDateString()}</p>
                  )}
                  {subscriptionExpiresAt && (
                    <p><span className="text-slate-400">{periodLabel(subscriptionStatus)}:</span> {fmtDateTime(subscriptionExpiresAt)}</p>
                  )}
                  <p><span className="text-slate-400">Verification:</span> {verificationStatus}</p>
                  {isCancelScheduled && (
                    <p className="text-slate-400">Your subscription will end at the date shown above. You can resume any time before then.</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                {isActive && (
                  <>
                    <button onClick={handleChangePlan} disabled={processing} className="btn-secondary">
                      Change plan (proration)
                    </button>
                    <button onClick={handleCancelInPortal} disabled={processing} className="btn-primary bg-red-600 hover:bg-red-700">
                      Cancel subscription
                    </button>
                  </>
                )}

                {isCancelScheduled && (
                  <>
                    <button onClick={handleBilling} disabled={processing} className="btn-primary">
                      Resume subscription
                    </button>
                    <button onClick={handleChangePlan} disabled={processing} className="btn-secondary">
                      Change plan
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------- Tier Card ---------- */
function TierCard({ tier, showCTA, disabled, onSelect, showVerifyHint, isCurrent }) {
  const features = {
    ENTRY: [
      "4 free standard tickets / year (max 1 per event)",
      "Permanent feature on 1 curated playlist",
      "Dedicated reel on Instagram, YouTube, TikTok, X",
      "Eligible for 'Top 5 Songs of the Week'",
      "Chance to be booked for future events",
      "+ More"
    ],
    MIDDLE: [
      "All Entry features",
      "9 free standard tickets / year (max 1 per event)",
      "Permanent features on 2 playlists",
      "Additional post across our social channels",
      "Invitations to perform at larger events",
      "Access to industry networking events",
      "Monthly access to studio (5 hours per month)",
      "+ More"
    ],
    HEADLINER: [
      "All Middle & Entry features",
      "Unlimited free tickets (max 1 per event)",
      "Opportunities to headline major events",
      "Advanced, personalised promo campaigns",
      "Monthly access to studio (10 hours per month)",
      "1-hour DJ set broadcast live",
      "Brand and licensing opportunities",
      "Priority UK + International booking considerations",
      "Priority on track submissisons",
      "+ More"
    ],
  }[tier.id];

  return (
    <div
      className={`card h-full flex flex-col relative ${
        tier.popular ? "ring-1 ring-brand-600/60" : ""
      }`}
    >
      {/* Current plan ribbon */}
      {isCurrent && (
        <div className="absolute -top-3 right-3 rounded-full px-3 py-1 text-xs bg-white/10 text-slate-100 border border-white/10">
          Current plan
        </div>
      )}

      {/* Header: name + price */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-2xl md:text-3xl font-semibold text-white leading-tight">
            {tier.name}
          </h3>
          <div className="mt-1 text-slate-300">{tier.blurb}</div>
        </div>

        <div className="text-right">
          {tier.popular && !isCurrent && (
            <div className="badge mb-1 bg-brand-600/20 text-slate-100">Most popular</div>
          )}
          <div className="font-display text-3xl md:text-4xl text-white leading-none">
            {fmtGBP(tier.priceMonthly)}
          </div>
          <div className="text-xs text-slate-400">per month</div>
        </div>
      </div>

      {/* Features list (fills space to keep buttons aligned) */}
      <ul className="mt-6 flex-1 list-disc list-inside space-y-2 text-slate-300 leading-relaxed">
        {features.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>

      {/* CTA area */}
      <div className="pt-6">
        {showCTA ? (
          <>
            <button
              disabled={disabled}
              onClick={onSelect}
              className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Subscribe
            </button>
            {showVerifyHint && (
              <p className="mt-2 text-xs text-slate-400">
                You can subscribe now. Benefits like free tickets activate after age verification.
              </p>
            )}
          </>
        ) : (
          // Keep spacing consistent when no button is shown
          <div className="h-10" />
        )}
      </div>
    </div>
  );
}
